import { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEquals, faTrash } from '@fortawesome/free-solid-svg-icons';
import style from './RatioCalculator.module.css';
import Card from '../../ui/Card';

const STORAGE_KEY = "zdtRatioCalculatorState";

// Safely read a stored value, falling back if it's missing or invalid JSON.
const loadStoredState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// Small reusable component: renders "numerator" stacked over "denominator"
// with a horizontal line between them.
const Fraction = ({ numerator, denominator }) => (
  <span className={style.fraction}>
    <span className={style.fractionTop}>{numerator}</span>
    <span className={style.fractionBottom}>{denominator}</span>
  </span>
);

// Maps which variable is being solved for to which two variables get
// multiplied (numerator) and which one is the divisor (denominator).
const FORMULA_MAP = {
  a: { num: ["b", "c"], denom: "d" },
  b: { num: ["a", "d"], denom: "c" },
  c: { num: ["a", "d"], denom: "b" },
  d: { num: ["b", "c"], denom: "a" },
};

const RatioCalculator = () => {
  // Lazily read localStorage once on first render, so the very first
  // paint already has the restored state (no flash of empty inputs).
  const stored = loadStoredState();

  const [findLabels, setFindLabels] = useState(stored?.findLabels ?? { a: "A", b: "B", c: "C", d: "D" });
  const [a, setA] = useState(stored?.a ?? "");
  const [b, setB] = useState(stored?.b ?? "");
  const [c, setC] = useState(stored?.c ?? "");
  const [d, setD] = useState(stored?.d ?? "");

  // Store the raw inputs used for the last calculation + which variable
  // was solved for, so we can re-derive labels/text on every render.
  const [calculation, setCalculation] = useState(stored?.calculation ?? null); // { solvedFor, a, b, c, d, value }

  // Whenever any of these change, persist the full state to localStorage.
  useEffect(() => {
    const stateToSave = { a, b, c, d, findLabels, calculation };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    } catch {
      // Ignore write errors (e.g. storage full or disabled)
    }
  }, [a, b, c, d, findLabels, calculation]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const filledFields = [a, b, c, d].filter((v) => v !== "");
    if (filledFields.length !== 3) {
      alert("Please fill exactly 3 fields.");
      return;
    }

    let solvedFor;
    let value;

    if (a === "") {
      solvedFor = "a";
      value = (b * c) / d;
    } else if (b === "") {
      solvedFor = "b";
      value = (a * d) / c;
    } else if (c === "") {
      solvedFor = "c";
      value = (a * d) / b;
    } else if (d === "") {
      solvedFor = "d";
      value = (b * c) / a;
    }

    setCalculation({ solvedFor, a, b, c, d, value });
  };

  // Resets all inputs and clears the last calculation/result/steps.
  const handleClear = () => {
    setA("");
    setB("");
    setC("");
    setD("");
    setCalculation(null);
  };

  // Derive everything needed to render the steps from the stored
  // calculation data, using the CURRENT findLabels every time we render.
  const buildOutput = () => {
    if (!calculation) return null;
    const { solvedFor, a, b, c, d, value } = calculation;

    const values = { a, b, c, d };
    const { num, denom } = FORMULA_MAP[solvedFor];
    const [num1, num2] = num;

    const solvedLabel = findLabels[solvedFor];

    return {
      result: `${solvedLabel} = ${value}`,
      solvedLabel,
      value,
      // Step 1 data: a/b = c/d
      leftNum: findLabels.a,
      leftDenom: findLabels.b,
      rightNum: findLabels.c,
      rightDenom: findLabels.d,
      // Step 2 data: labels only, e.g. B = (A × D) / C
      formulaNumLabel: `${findLabels[num1]} × ${findLabels[num2]}`,
      formulaDenomLabel: findLabels[denom],
      // Step 3 data: actual numbers substituted in
      formulaNumValue: `${values[num1]} × ${values[num2]}`,
      formulaDenomValue: values[denom],
    };
  };

  const output = buildOutput();

  return (
    <div>
      <div className={style.header}>
        <div>
          <h2>Ratio Calculator</h2>
        </div>
        <div className={style['label-select']}>
          <label style={{ fontSize: '12px' }}>Variable Labels:</label>
          <select
            className="form-control form-control_sm"
            value={`${findLabels.a}, ${findLabels.b}, ${findLabels.c}, ${findLabels.d}`}
            onChange={(e) => {
              const labels = e.target.value.split(",").map((s) => s.trim());
              setFindLabels({ a: labels[0], b: labels[1], c: labels[2], d: labels[3] });
            }}
          >
            <option value="A, B, C, D">A, B, C, D</option>
            <option value="x, y, z, w">x, y, z, w</option>
            <option value="1st, 2nd, 3rd, 4th">1st, 2nd, 3rd, 4th</option>
          </select>
        </div>
      </div>
      <div className={style['form-container']}>
        <div>
          <Card>
            <form onSubmit={handleSubmit}>
              <div className={style.inputGrid}>
                <div className={style.inputSet}>
                  <div className={`${style['ratio_input-item']} form-group_2`}>
                    <div className="form-control_2">
                      <label>{findLabels.a}:</label>
                      <input className="form-control_2_input" type="number" value={a} onChange={(e) => setA(e.target.value)} />
                    </div>
                  </div>
                  <div className={`${style['ratio_input-item']} form-group_2`}>
                    <div className="form-control_2">
                      <label>{findLabels.b}:</label>
                      <input className="form-control_2_input" type="number" value={b} onChange={(e) => setB(e.target.value)} />
                    </div>
                  </div>
                </div>
                <div className={style.inputSet}>
                  <FontAwesomeIcon className="icon" icon={faEquals} />
                </div>
                <div className={style.inputSet}>
                  <div className={`${style['ratio_input-item']} form-group_2`}>
                    <div className="form-control_2">
                      <label>{findLabels.c}:</label>
                      <input className="form-control_2_input" type="number" value={c} onChange={(e) => setC(e.target.value)} />
                    </div>
                  </div>
                  <div className={`${style['ratio_input-item']} form-group_2`}>
                    <div className="form-control_2">
                      <label>{findLabels.d}:</label>
                      <input className="form-control_2_input" type="number" value={d} onChange={(e) => setD(e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="spacer"></div>
              <div className={`center ${style.buttonRow}`}>
                <button className="button button-sm button-primary" type="submit">Calculate</button>
                {output && (
                  <button className="button button-sm" type="button" onClick={handleClear}>
                    <FontAwesomeIcon className="icon" icon={faTrash} />
                    Clear
                  </button>
                )}
              </div>
            </form>
          </Card>
        </div>

        <div>
          {output && (
            <div className={style.output}>
              <h3>Result</h3>
              <p className={style['output-result']}>{output.result}</p>
            </div>
          )}
          {output && (
            <div className={style.output}>
              <h3>Solution Steps</h3>
              <div className={style.stepsContainer}>
                <div className={style.step}>
                  <p className={style.stepTitle}>Step 1: Start with the proportion</p>
                  <div className={style.equationRow}>
                    <Fraction numerator={output.leftNum} denominator={output.leftDenom} />
                    <span className={style.equalsSign}>=</span>
                    <Fraction numerator={output.rightNum} denominator={output.rightDenom} />
                  </div>
                </div>

                <div className={style.step}>
                  <p className={style.stepTitle}>Step 2: Solve for {output.solvedLabel}</p>
                  <div className={style.equationRow}>
                    <span>{output.solvedLabel}</span>
                    <span className={style.equalsSign}>=</span>
                    <Fraction numerator={output.formulaNumLabel} denominator={output.formulaDenomLabel} />
                  </div>
                </div>

                <div className={style.step}>
                  <p className={style.stepTitle}>Step 3: Substitute values</p>
                  <div className={style.equationRow}>
                    <span>{output.solvedLabel}</span>
                    <span className={style.equalsSign}>=</span>
                    <Fraction numerator={output.formulaNumValue} denominator={output.formulaDenomValue} />
                  </div>
                </div>

                <div className={style.step}>
                  <p className={style.stepTitle}>Step 4: Final Answer</p>
                  <div className={style.equationRow}>
                    <span>{output.solvedLabel}</span>
                    <span className={style.equalsSign}>=</span>
                    <span>{output.value}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RatioCalculator;